import { Layout, Typography, Card, Space, Button, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import './PaymentSuccess.scss';
import { AppRoutes } from 'src/enums/ERoutes';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { resellerLogin } from '@/api/reseller';
import { setReseller } from '@/store/reseller/slice';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import { useMutation } from '@tanstack/react-query';
import { unsetOnboardingInfo } from '@/store/resellerOnboardingSteps/slice';
import { Reseller } from '@/types/TReseller';
import { USER_TYPES } from 'src/enums/EUser';
import { useClarity } from 'src/Hooks/useClarity';

interface ResellerOnboardingData {
	email: string;
	password: string;
	token?: string;
}

const { Content } = Layout;
const { Title, Text } = Typography;

export default function PaymentSuccessForReseller() {
	const navigate = useNavigate();
	const clarity = useClarity();

	useEffect(() => {
		posthog.capture('Reseller_Payment_Success');
		clarity('event', 'Reseller_Payment_Success');
	}, []);

	const dispatch = useAppDispatch();

	const mutation = useMutation({
		mutationKey: ['resellerLogin'],
		mutationFn: resellerLogin,
		onSuccess(data) {
			dispatch(setReseller(data?.data as Reseller));
			LocalStorageUtility.setLocalData(LocalStorageKeys.RESELLER_AUTH_TOKEN, data?.token);

			// remove the onboarding data from local storage
			dispatch(unsetOnboardingInfo());

			navigate(AppRoutes.RESELLER_ONBOARDING);
		},
		onError(data) {
			notification.error({
				message: data.message,
			});
		},
	});

	const handleSubmit = () => {
		const data = LocalStorageUtility.getLocalData(
			LocalStorageKeys.RESELLER_ONBOARDING,
		) as ResellerOnboardingData;

		if (data?.token) {
			LocalStorageUtility.setLocalData(LocalStorageKeys.RESELLER_AUTH_TOKEN, data?.token);
			navigate(AppRoutes.RESELLER_PORTAL);
		} else if (data?.email && data?.password) {
			mutation.mutate({
				username: data?.email,
				password: data?.password,
				from: USER_TYPES.RESELLER,
			});
		} else {
			console.log('NO EMAIL OR PASSWORD');
			navigate(AppRoutes.RESELLER_PORTAL);
		}
	};

	return (
		<Layout className="payment-success-container">
			<Content>
				<Card bordered={false} className="payment-success-card">
					<Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
						{/* Checkmark icon at the top */}
						<div className="success-icon">
							<span className="checkmark">✓</span>
						</div>

						<Title level={2}>Welcome to the best white label program on the market</Title>

						<Text>
							Your free trial has <Text strong>officially started</Text>. Really happy to have you
							with us.
						</Text>

						<div style={{ textAlign: 'left', marginTop: '2rem' }}>
							<Title level={4}>Here's what happens next:</Title>

							<Space direction="vertical" size="large" style={{ width: '100%', marginTop: '1rem' }}>
								<div>
									<Space align="start">
										<div className="step-number">1</div>
										<div>
											<Text strong>You are ready to go</Text>
											<br />
											<Text type="secondary">
												Log in using the email you provided during payment and the password you
												created in the previous step
											</Text>
											<br />
											<Text strong className="!text-gray-600">
												Start exploring your Admin Dashboard
											</Text>
										</div>
									</Space>
								</div>

								<div>
									<Space align="start">
										<div className="step-number">2</div>
										<div>
											<Text strong>Access your dashboard</Text>
											<br />
											<Text type="secondary">
												You can chat with us or book your first call within your dashboard.
											</Text>
											<br />
											<Text strong className="!text-gray-600">
												Schedule your onboarding call anytime
											</Text>
										</div>
									</Space>
								</div>
							</Space>
						</div>

						<Button
							type="primary"
							size="large"
							block
							onClick={handleSubmit}
							style={{ marginTop: '2rem' }}
						>
							Login to your admin dashboard
						</Button>
					</Space>
				</Card>
			</Content>
		</Layout>
	);
}
